import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, Building, Shield, Scale } from 'lucide-react';

const AGB = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Redirect to German version if not already on it, but only once
  React.useEffect(() => {
    // Check if we're already on the German AGB path
    if (location.pathname === '/de/agb') {
      return; // Already on the correct path, no need to redirect
    }
    
    // Only redirect if we're not on German and not already redirecting
    if (currentLanguage !== 'de' && !location.pathname.includes('/de/agb')) {
      navigate('/de/agb', { replace: true });
    }
  }, [currentLanguage, navigate, location.pathname]);

  // Don't render anything while redirecting
  if (currentLanguage !== 'de' && location.pathname !== '/de/agb') {
    return null;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container-custom py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <FileText className="h-12 w-12 text-primary-600" />
            </div>
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">
              Allgemeine Geschäftsbedingungen
            </h1>
            <p className="text-lg text-neutral-600">
              Stand: 15. Juli 2025
            </p>
          </div>

          {/* Company Info */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
              <Building className="h-6 w-6 text-primary-600 mr-3" />
              Anbieter
            </h2>
            <div className="space-y-2 text-neutral-700">
              <p><strong>surf & game GmbH</strong></p>
              <p>Amtsgericht Charlottenburg (Berlin) HRB 109460 B</p>
              <p>Hasenheide 115</p>
              <p>D-10967 Berlin</p>
              <p>Deutschland</p>
              <p className="mt-4">
                <strong>E-Mail:</strong> info@co2neutralpage.com
              </p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* § 1 Geltungsbereich */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 1 Geltungsbereich
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Diese Allgemeinen Geschäftsbedingungen gelten für alle 
                  Geschäftsbeziehungen zwischen der surf & game GmbH 
                  (nachfolgend "Anbieter") und den Kunden (nachfolgend "Kunde") 
                  in der jeweiligen Fassung zum Zeitpunkt der Bestellung.
                </p>
                <p className="mt-4">
                  Abweichende Bedingungen des Kunden werden nicht anerkannt, 
                  es sei denn, der Anbieter stimmt ihrer Geltung ausdrücklich zu.
                </p>
              </div>
            </div>

            {/* § 2 Vertragsschluss */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 2 Vertragsschluss
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Der Vertrag kommt durch Angebot und Annahme zustande. 
                  Das Angebot des Anbieters erfolgt durch die Darstellung 
                  der Dienstleistungen auf der Website.
                </p>
                <p className="mt-4">
                  Der Kunde kann das Angebot durch Ausfüllen des Bestellformulars 
                  und Absenden der Bestellung annehmen. Der Anbieter bestätigt 
                  den Eingang der Bestellung unverzüglich per E-Mail.
                </p>
                <p className="mt-4">
                  Der Vertrag kommt erst durch die ausdrückliche Bestätigung 
                  des Anbieters zustande.
                </p>
              </div>
            </div>

            {/* § 3 Leistungsbeschreibung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 3 Leistungsbeschreibung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Der Anbieter bietet folgende Dienstleistungen an:
                </p>
                <ul className="list-disc pl-6 space-y-2 mt-4">
                  <li>CO₂-Neutralitäts-Zertifizierung für Websites</li>
                  <li>Karbon-Fußabdruck-Berechnung</li>
                  <li>CO₂-Neutralitäts-Badges für Websites</li>
                  <li>Sertifikatsverifizierung</li>
                  <li>Beratung zu nachhaltigen Technologielösungen</li>
                </ul>
                <p className="mt-4">
                  Die genauen Leistungsdetails ergeben sich aus der jeweiligen 
                  Produktbeschreibung auf der Website.
                </p>
              </div>
            </div>

            {/* § 4 Preise und Zahlungsbedingungen */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 4 Preise und Zahlungsbedingungen
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Alle Preise verstehen sich in Euro inklusive der gesetzlichen 
                  Mehrwertsteuer.
                </p>
                <p className="mt-4">
                  Die Zahlung erfolgt per Banküberweisung, Kreditkarte oder 
                  PayPal. Die Rechnung ist innerhalb von 14 Tagen nach 
                  Rechnungsstellung zur Zahlung fällig.
                </p>
                <p className="mt-4">
                  Bei Zahlungsverzug behält sich der Anbieter das Recht vor, 
                  Verzugszinsen in Höhe von 5 Prozentpunkten über dem 
                  Basiszinssatz zu berechnen.
                </p>
              </div>
            </div>

            {/* § 5 Leistungserbringung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 5 Leistungserbringung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Der Anbieter verpflichtet sich, die bestellten Leistungen 
                  mit der erforderlichen Sorgfalt und innerhalb der vereinbarten 
                  Fristen zu erbringen.
                </p>
                <p className="mt-4">
                  Die Leistungserbringung erfolgt in der Regel elektronisch 
                  per E-Mail oder über das Kundenportal.
                </p>
                <p className="mt-4">
                  Der Anbieter behält sich vor, bei unvorhersehbaren Umständen 
                  die Leistungserbringung zu verzögern oder zu unterbrechen.
                </p>
              </div>
            </div>

            {/* § 6 Gewährleistung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Shield className="h-6 w-6 text-primary-600 mr-3" />
                § 6 Gewährleistung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Der Anbieter gewährleistet, dass die erbrachten Leistungen 
                  den vereinbarten Spezifikationen entsprechen.
                </p>
                <p className="mt-4">
                  Bei Mängeln hat der Kunde das Recht auf Nacherfüllung, 
                  Minderung oder Rücktritt vom Vertrag nach den gesetzlichen 
                  Bestimmungen.
                </p>
                <p className="mt-4">
                  Die Gewährleistungsfrist beträgt 24 Monate ab Leistungserbringung.
                </p>
              </div>
            </div>

            {/* § 7 Haftung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4 flex items-center">
                <Scale className="h-6 w-6 text-primary-600 mr-3" />
                § 7 Haftung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Der Anbieter haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit 
                  sowie nach dem Produkthaftungsgesetz.
                </p>
                <p className="mt-4">
                  Für leichte Fahrlässigkeit haftet der Anbieter nur bei 
                  Schäden aus der Verletzung des Lebens, des Körpers oder 
                  der Gesundheit sowie bei Schäden aus der Verletzung 
                  wesentlicher Vertragspflichten.
                </p>
                <p className="mt-4">
                  Die Haftung für Schäden, die nicht in den vorgenannten 
                  Kategorien fallen, ist auf den typischerweise vorhersehbaren 
                  Schaden begrenzt.
                </p>
              </div>
            </div>

            {/* § 8 Datenschutz */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 8 Datenschutz
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Die Erhebung, Verarbeitung und Nutzung personenbezogener 
                  Daten erfolgt nach den Bestimmungen der Datenschutzerklärung, 
                  die Bestandteil dieser AGB ist.
                </p>
                <p className="mt-4">
                  Der Kunde erklärt sich mit der Erhebung, Verarbeitung und 
                  Nutzung seiner Daten nach Maßgabe der Datenschutzerklärung einverstanden.
                </p>
              </div>
            </div>

            {/* § 9 Kündigung */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 9 Kündigung
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Bei Dauerschuldverhältnissen kann der Vertrag von beiden 
                  Parteien mit einer Frist von 30 Tagen zum Monatsende 
                  gekündigt werden.
                </p>
                <p className="mt-4">
                  Das Recht zur außerordentlichen Kündigung aus wichtigem 
                  Grund bleibt unberührt.
                </p>
                <p className="mt-4">
                  Die Kündigung erfolgt schriftlich oder per E-Mail.
                </p>
              </div>
            </div>

            {/* § 10 Schlussbestimmungen */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                § 10 Schlussbestimmungen
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Es gilt das Recht der Bundesrepublik Deutschland unter 
                  Ausschluss des UN-Kaufrechts.
                </p>
                <p className="mt-4">
                  Erfüllungsort und Gerichtsstand ist Berlin, soweit der 
                  Kunde Kaufmann ist.
                </p>
                <p className="mt-4">
                  Sollten einzelne Bestimmungen dieser AGB unwirksam sein, 
                  bleibt der Vertrag im Übrigen wirksam.
                </p>
                <p className="mt-4">
                  Der Anbieter behält sich vor, diese AGB jederzeit zu ändern. 
                  Änderungen werden dem Kunden rechtzeitig mitgeteilt.
                </p>
              </div>
            </div>

            {/* Kontakt */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">
                Kontakt
              </h2>
              <div className="prose prose-neutral max-w-none">
                <p>
                  Bei Fragen zu diesen Allgemeinen Geschäftsbedingungen 
                  wenden Sie sich bitte an:
                </p>
                <div className="bg-neutral-50 p-4 rounded-lg mt-4">
                  <p><strong>surf & game GmbH</strong></p>
                  <p>Hasenheide 115</p>
                  <p>D-10967 Berlin</p>
                  <p>Deutschland</p>
                  <p className="mt-2">
                    <strong>E-Mail:</strong> info@co2neutralpage.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AGB; 